import { apiService } from "./api.service";
import api_no_token_Service from "./api_no_token.service";
import { PaginatedResponse } from "types/index";
import { Package } from "types/package";
import { normalizePaginatedResponse } from "utils/pagination";
import { unwrapEntity, unwrapOrThrow } from "utils/apiResponse";

class PackagesService {
  async getAll(includeIds?: string | number | (string | number)[]): Promise<Package[]> {
    const idsParam =
      includeIds == null
        ? undefined
        : Array.isArray(includeIds)
          ? includeIds.map(String).filter(Boolean).join(",")
          : String(includeIds);
    const query = idsParam ? `?includeIds=${encodeURIComponent(idsParam)}` : "";
    const response = await api_no_token_Service.get<any>(`/package/GetAll${query}`, {
      // Do not trigger global loader when fetching packages
      // to avoid blocking UI with a page-level spinner.
      metadata: { skipGlobalLoading: true },
    } as any);

    return this.normalizePackageList(response);
  }

  /**
   * Activated packages for back-office selection (includes private packages).
   * Requires an authenticated session.
   */
  async getAllForBackOffice(
    includeIds?: string | number | (string | number)[]
  ): Promise<Package[]> {
    const idsParam =
      includeIds == null
        ? undefined
        : Array.isArray(includeIds)
          ? includeIds.map(String).filter(Boolean).join(",")
          : String(includeIds);
    const query = idsParam ? `?includeIds=${encodeURIComponent(idsParam)}` : "";
    const response = await apiService.get<any>(
      `/package/GetAllForBackOffice${query}`,
      {
        metadata: { skipGlobalLoading: true },
      } as any
    );

    return this.normalizePackageList(response);
  }

  private normalizePackageList(response: unknown): Package[] {
    const payload: any = (response as any)?.data ?? response;
    if (Array.isArray(payload)) {
      return payload as Package[];
    }
    if (Array.isArray((payload as any)?.data)) {
      return (payload as any).data as Package[];
    }
    if (Array.isArray((response as any)?.items)) {
      return (response as any).items as Package[];
    }
    throw new Error((response as any)?.message || "Failed to load packages");
  }

  async list(
    page: number = 1,
    limit: number = 100,
    options?: {
      search?: string;
      isActivated?: boolean;
      isPublic?: boolean;
    }
  ): Promise<PaginatedResponse<Package>> {
    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(limit),
    });
    if (options?.search?.trim()) {
      params.set("search", options.search.trim());
    }
    if (options?.isActivated !== undefined) {
      params.set("isActivated", String(options.isActivated));
    }
    if (options?.isPublic !== undefined) {
      params.set("isPublic", String(options.isPublic));
    }

    const response = await apiService.get<any>(
      `/package/GetPageList?${params.toString()}`
    );

    return normalizePaginatedResponse<Package>(response, {
      page,
      limit,
      fallbackMessage: "Failed to load packages",
    });
  }

  async getById(id: string): Promise<Package> {
    const response = await apiService.get<any>(`/package/${id}`);
    const payload: any = (response as any)?.data ?? response;

    const packageData =
      payload?.data && typeof payload.data === "object"
        ? payload.data
        : typeof payload === "object"
        ? payload
        : null;

    if (packageData) {
      return packageData as Package;
    }

    return response as unknown as Package;
  }

  async create(payload: Partial<Package>): Promise<Package> {
    const response = await apiService.post<Package>(`/package`, payload);
    return unwrapEntity<Package>(response);
  }

  async update(id: string, payload: Partial<Package>): Promise<Package> {
    const response = await apiService.put<Package>(`/package/${id}`, payload);
    return unwrapEntity<Package>(response);
  }

  async remove(id: string): Promise<void> {
    const response = await apiService.delete(`/package/${id}`);
    unwrapOrThrow(response, "Delete failed");
  }
}

export const packagesService = new PackagesService();
export default packagesService;
